import React, { useEffect, useMemo, useCallback, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { TableCellsWithPermissions } from '@vlabs/shared/components/table/cells';
import { permissions } from '@vlabs/shared/config';
import { actionColumnProps } from '@vlabs/shared/utils';
import { Table, Panel, openConfirmPopup } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import { selectAnalyticData } from '@vlabs/pages/video-analytics/selectors';

import { setPageIndex as setPageIndexAction, setPageSize as setPageSizeAction } from '../store';
import {
  StatusLabelCell,
  AnalyticNameCell,
  StreamIdCell,
  StartStreamCell,
  StopStreamCell,
  StreamGroupsCell,
  EditStreamCell,
  DuplicateStreamCell,
  StreamNameCell,
  StreamDescriptionCell } from './components/cells/Cell';
import { MapCell } from './components/map/MapCell';
import st from './StreamsPage.module.sass';

const DeleteCellWithProps = (props) => (
  <TableCellsWithPermissions.DeleteCell
    {...props}
    buttonProps={{ variant: 'flat' }}
  />
);

function StreamsPage({ filters, pageIndex, pageSize, setPageIndex, setPageSize, pageSizeOptions }) {
  const { t } = useTranslation();
  const [streamsData, setStreamsData] = useState([]);

  const fetchStreams = useCallback(async ({ page = pageIndex, pageSize: ps = pageSize } = {}) => {
    const params = {
      // FIXME кмк это нужно делать в схеме в апи биндингах
      page: page + 1,
      page_size: ps,
      statuses: filters?.statuses?.map(({ value }) => value) || undefined,
      stream_ids: filters?.stream_ids?.map(({ value }) => value) || undefined,
      group_name: filters?.group_name?.value || undefined,
    };
    const resp = await apiContainer.lunaClient.streams.getPage(params);
    setStreamsData(resp?.data ?? []);
  }, [filters, pageIndex, pageSize]);

  useEffect(() => { fetchStreams(); }, [fetchStreams]);

  useEffect(() => {
    const scrollContainer = document.querySelector('.Table__ScrollContainer');
    const header = document.querySelector('.TableHead');
    const headerWrapper = document.querySelector('.PageHeaderWrapper');
    let prevPageYOffset = 0;

    function hideOrVisibleHeader() {
      const currentPageYOffset = scrollContainer.scrollTop;
      const startTrigger = currentPageYOffset > 25;
      const isScrollingDown = currentPageYOffset > prevPageYOffset;

      headerWrapper.classList.toggle(st.HeaderHide, isScrollingDown && startTrigger);
      scrollContainer.classList.toggle(st.ScrollContainer_expanded, isScrollingDown && startTrigger);
      header.classList.toggle(st.HeaderWithShadow, startTrigger);

      if (!startTrigger) {
        header.classList.remove(st.HeaderWithShadow, st.HeaderHide);
      }

      prevPageYOffset = currentPageYOffset;
    }

    scrollContainer.addEventListener('scroll', hideOrVisibleHeader);
  }, []);

  const onDeleteHandler = (streams) => {
    const message = t('videoAnalytics:buttons.removing_stream_message');
    const onConfirm = async () => {
      await apiContainer.lunaClient.streams.delete(streams.stream_id);
      await fetchStreams();
      toast.success(t('videoAnalytics:buttons.removing_done_stream_message'));
    };
    openConfirmPopup({
      title: t('videoAnalytics:buttons.removing_stream'),
      message,
      type: 'delete',
      onConfirm,
      confirmButtonProps: {
        'data-testid': 'videoanalytics.confirmDeleteButton',
      },
    });
  };

  const columns = useMemo(() => ([
    actionColumnProps({
      id: 'start',
      accessor: (row) => ({ stream_id: row.stream_id, status: row.status, onReset: fetchStreams }),
      Cell: StartStreamCell,
    }),
    actionColumnProps({
      id: 'stop',
      accessor: (row) => ({ stream_id: row.stream_id, status: row.status, onReset: fetchStreams }),
      Cell: StopStreamCell,
    }),
    { Header: t('videoAnalytics:table_header.name'), accessor: 'name', Cell: StreamNameCell, minWidth: 80 },
    { Header: t('videoAnalytics:table_header.status'), accessor: 'status', Cell: StatusLabelCell, minWidth: 100 },
    { Header: t('videoAnalytics:table_header.videoanalytics'), accessor: 'analytics', Cell: AnalyticNameCell, minWidth: 350 },
    { Header: t('videoAnalytics:table_header.location'), accessor: 'location.geo_position', Cell: MapCell, minWidth: 220 },
    { Header: t('videoAnalytics:table_header.stream id'), accessor: 'stream_id', Cell: StreamIdCell },
    { Header: t('videoAnalytics:table_header.group'), accessor: 'groups', Cell: StreamGroupsCell },
    { Header: t('videoAnalytics:table_header.description'), accessor: 'description', Cell: StreamDescriptionCell },
    actionColumnProps({
      id: 'edit',
      accessor: ({ stream_id, analytics }) => ({ analytics, streamId: stream_id }),
      Cell: EditStreamCell,
    }),
    actionColumnProps({
      id: 'duplicate',
      accessor: 'stream_id',
      Cell: DuplicateStreamCell,
    }),
    actionColumnProps({
      id: 'delete',
      accessor: 'stream_id',
      Cell: DeleteCellWithProps }),
  ]), [t, fetchStreams]);

  return (
    <Panel className={st.CustomPanel}>
      <Table
        columns={columns}
        data={streamsData}
        onActions={{
          onDeleteRow: { handler: onDeleteHandler, permissions: { rules: [permissions.videoanalytics.deletion] } },
        }}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        paginationType="offsetBased"
        rowProps={({ original }) => ({ 'data-stream-id': original?.stream_id })}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
      />
    </Panel>
  );
}

export default connect((state) => ({
  filters: selectAnalyticData(state, 'filters'),
  pageIndex: selectAnalyticData(state, 'pageIndex'),
  pageSize: selectAnalyticData(state, 'pageSize'),
  pageSizeOptions: selectAnalyticData(state, 'pageSizeOptions'),
}),
(dispatch) => ({
  setPageIndex: (pageIndex) => dispatch(setPageIndexAction({ pageIndex })),
  setPageSize: (pageSize) => dispatch(setPageSizeAction({ pageSize })),
}),
)(StreamsPage);
